/* eslint-disable @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call */
import { Injectable } from '@angular/core'
import { filter, map, mapTo, share, switchMap, tap } from 'rxjs/operators'
import { merge, Observable, of } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { Feature, FeatureCollection, GeoJsonProperties } from 'geojson'

import { environment } from '@environments/environment'
import { ApiService } from '@shared/services/api.service'
import { vehicleSettings } from '@app/core/constants/vehicle-settings'
import { VehicleSetting } from './map-details/shared/vehicle-setting.model'
import { VehicleRoute } from '@shared/models/vehicle.model'
import { DeviceStatus } from '@shared/models/device.model'
import { Route, RoutesHistory } from '@shared/models/route.model'
import { Coordinate } from '@shared/models/coordinate.model'
import { mapSettings } from '@app/core/constants/map-settings'
import { MapSettings } from './shared/models/map-settings.model'
import { Dayjs } from 'dayjs'
import { Setting } from '@app/shared/models/setting.model'

@Injectable()
export class MapApiService {
  private count = 0

  private addressCache = new Map<string, string>()
  private lastRequestTime = 0

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {}

  getSettings(): Observable<VehicleSetting[]> {
    const settings$ = this.apiService.get(`/storage/settings`).pipe(share())

    const inputMap = {
      driver: true,
      groups: true,
      depot: true,
      areas: true,
    }
    const changeIsShown = (setting: VehicleSetting) => {
      // eslint-disable-next-line no-prototype-builtins
      if (!inputMap.hasOwnProperty(setting.property)) return
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      setting.isShown = inputMap[setting.property]
    }

    const validSettings$ = settings$.pipe(
      filter((resp) => resp['value']),
      map((resp) => JSON.parse(resp['value'])),
    )

    const invalidSettings$ = settings$.pipe(
      filter((resp) => !resp['value']),
      mapTo(vehicleSettings),
    )

    return merge(validSettings$, invalidSettings$).pipe(tap((settings) => settings.forEach(changeIsShown)))
  }

  getMapSettings(): Observable<MapSettings> {
    const settings$ = this.apiService.get(`/storage/mapsetting`).pipe(share())

    const validSettings$ = settings$.pipe(
      filter((resp) => resp['value']),
      map((resp) => JSON.parse(resp['value'])),
    )

    const invalidSettings$ = settings$.pipe(
      filter((resp) => !resp['value']),
      mapTo(mapSettings),
    )

    return merge(validSettings$, invalidSettings$)
  }

  updateMapSettings(mapSettings: MapSettings) {
    return this.apiService.post(`/storage/`, { key: 'mapsetting', value: JSON.stringify(mapSettings) })
  }

  updateSettings(settings: VehicleSetting[]) {
    return this.apiService.post(`/storage/`, { key: 'settings', value: JSON.stringify(settings) })
  }

  getLocationByCoordinates(coordinates: Coordinate): Observable<string> {
    const { lat, lng } = coordinates
    const key = `${lat},${lng}`

    if (this.addressCache.has(key)) {
      return of(this.addressCache.get(key)!)
    }

    const access_token = environment.mapBoxAccessToken
    const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&access_token=${access_token}`

    const now = Date.now()
    const delay = Math.max(0, 300 - (now - this.lastRequestTime))
    this.lastRequestTime = now + delay

    return of(null).pipe(
      switchMap(() =>
        this.http.get<any>(url).pipe(
          map((response) => {
            const features = response?.features || []
            if (!features.length) return 'Unknown location'

            const address =
              features[0]?.properties?.full_address || features[0]?.properties?.name || features[0]?.place_name || 'Unknown location'

            this.addressCache.set(key, address)
            return address
          }),
        ),
      ),
    )
  }

  getRoutesHistory(path: string, dateFrom: Dayjs, dateTo: Dayjs): Observable<RoutesHistory> {
    const fields = ['summary']

    return this.getRoutes<RoutesHistory>(path, dateFrom, dateTo, fields, true).pipe(
      map(({ routes, ...rest }: RoutesHistory) => ({
        routes: routes.map(this.addIndexStop).map(this.toVehicleRoute),
        ...rest,
      })),
      tap(() => (this.count = 0)),
    )
  }

  getRouteList(path: string, dateFrom: Dayjs, dateTo: Dayjs): Observable<VehicleRoute[]> {
    return this.getRoutes<VehicleRoute[]>(path, dateFrom, dateTo, null, true).pipe(
      map((routes: VehicleRoute[]) => routes.map(this.addIndexStop).map(this.toVehicleRoute)),
      tap(() => (this.count = 0)),
    )
  }

  getRoutes<T>(path: string, dateFrom: Dayjs, dateTo: Dayjs, fields?: string[], isNoGroup = false): Observable<T> {
    const defaultFields = [
      'startArea',
      'finishArea',
      'coordinates',
      'coordinates.speed',
      'coordinates.externalVoltage',
      'coordinates.temperatureLevel',
      'coordinates.movement',
      'coordinates.ignition',
    ]
    const mergedFields = fields ? defaultFields.concat(fields) : defaultFields
    const params = {
      dateFrom: dateFrom.format(),
      dateTo: dateTo.format(),
      'fields[]': mergedFields,
      isNoGroup,
    }

    return this.apiService.get(path, { params }).pipe(map((resp) => resp['data']))
  }

  private addIndexStop = (vehicleRoute) => {
    if (vehicleRoute.type === DeviceStatus.Stopped) {
      this.count++
      vehicleRoute.indexStop = this.count.toString()
    }

    return vehicleRoute
  }

  private toVehicleRoute = (vehicleRoute) => new VehicleRoute(vehicleRoute)

  getCameraVisibilityInMap(): Observable<boolean> {
    return this.apiService.get(`/settings/shouldShowCameraInMap`).pipe(
      map((resp) => resp['data']),
      map((setting: Setting) => setting?.value ?? false),
    )
  }
}
